/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Button, Checkbox } from '@mui/material';
import { FlexGrid, SelectionMode } from '@grapecity/wijmo.grid';
import { Selector } from '@grapecity/wijmo.grid.selector';
import { ContainerLayout } from 'components/layouts/ContainerLayout';
import { toggleClass } from '@grapecity/wijmo';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  GlobalBtnGroup,
} from 'components/layouts/ContentLayout';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import { IconButton } from 'components/buttons/IconSVG';
import { useTranslation } from 'react-i18next';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { ComboBox, MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import CustomGrid from 'components/grids/CustomGrid';
import { getExcelFileName } from 'utils/ExcelUtil';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { useNavigate } from 'react-router-dom';
import { Code } from 'models/common/CommonCode';
import { CellType, DataMap, KeyAction } from '@grapecity/wijmo.grid';
import { hasRole } from 'utils/SessionUtil';
import { CrudCode } from 'models/common/Edit';
import useEvent from 'react-use-event-hook';
import _ from 'lodash';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { st } from 'components/inputs/CustomInput';
import { SearchParam, UtMatrixEumTagId } from 'models/ut/UtMatrixEumTagId';
import StandardEquipmentPopup from './popup/StandardEquipmentPopup';
import {
  checkDuplicationEumTagMapping,
  getUtMatrixEumTagIdList,
  saveUtMatrixEumTagList,
} from 'apis/ut/UtMatrixEumTag';
import { GatingContListPopUp } from 'pages/gtng/popup/GatingContListPopUp';
import { CommonUtil } from 'utils/CommonUtil';
import { Key } from '@mui/icons-material';

/**
 * @description UT Matrix EUM Tag Id
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.02   jihwan89       [고도화] EUM TAG 페이지 추가
 * 2025.10.21   jihwan89       [고도화] 단위-UT항목-표준설비코드 중복등록불가, 표준설비코드 공백저장 추가
 */
const UtMatrixEumTagIdPage = () => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const navigate = useNavigate();

  const gridRef = useRef<any>();
  const [hitTest, setHitTest] = useState<any>();
  const [flexRef, setFlexRef] = useState<any>();
  const [flexItem, setFlexItem] = useState<any>();
  const [hasAuth, setHasAuth] = useState<boolean>(false);

  const [rowData, setRowData] = useState<UtMatrixEumTagId[]>([]);
  const [checkedItems, setCheckedItems] = useState<UtMatrixEumTagId[]>();

  const [copCdCode, setCopCd] = useState<Code[]>([]); // 법인코드
  const [utmTpNmCode, setUtmTpNm] = useState<Code[]>([]); // UT항목명 코드
  const [utmTpNmCdValue, setUtmTpNmValue] = useState<string>(''); // 일괄지정용 UT 항목

  const [standardEquipmentCondition, setStandardEquipmentCondition] = useState<any>({
    singleSelect: true,
  });
  const [isStandardEquipmentModalOpen, setStandardEquipmentModalOpen] = useState<boolean>(false);

  const initSearchParam = {
    copCd: '',
    tagId: '',
    tagNm: '',
    utmTpNm: '',
    msrUnitCd: '',
    stndEqpIdYn: '',
    copCds: [],
    utmTpNms: [],
  };

  const [searchParam, setSearchParam] = useState<SearchParam>(initSearchParam);

  const [userIds, setUserIds] = useState<string>('');
  const [isOpenUserReadModal, setOpenUserReadModal] = useState<any>(false);

  const isReadOnly = useMemo(() => {
    return hasAuth;
  }, [hasAuth]);

  useEffect(() => {
    initCondition();
    if (hasRole('ADM') || hasRole('UT_MANAGER')) {
      setHasAuth(true);
    }
  }, []);

  const initCondition = async () => {
    const utmTpNm = await getCommonCodeNames('UTM_ITEM_CD');
    const copCd = await getCommonCodeNames('ELM_COP_CD');

    const utmTpNms = utmTpNm.filter((item) => {
      return item.cmnCd === 'CDA' || item.cmnCd === 'ELECTRICITY';
    });
    setUtmTpNm(utmTpNms);
    setCopCd(copCd);

    btnSearch();
  };

  const onBtUtmTpNmAll = () => {
    if ((checkedItems || []).length < 1) {
      openMessageBar({
        type: 'error',
        content: t('gtng.msg.일괄적용할 항목을 선택해주세요.', '일괄적용할 항목을 선택해주세요.'),
      });
      return;
    }
    (checkedItems || []).forEach((o) => {
      // if (o.asstRvwTpCd !== 'RLT_INS') return;
      o.utmTpNm = utmTpNmCdValue;
      o.crudKey = CrudCode.UPDATE;
    });
    gridRef.current?.collectionView?.refresh();
  };

  const layoutDefinition = useMemo(() => {
    const utmTmNmCodeMap = new DataMap(utmTpNmCode, 'cmnCd', 'cmnCdNm');
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        isReadOnly: true,
        align: 'center',
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        width: 43,
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'copCd',
        header: String(t('ut.label.법인', '법인')),
        width: 110,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'tagId',
        header: String(t('ut.label.Tag ID', 'Tag ID')),
        width: 210,
        isReadOnly: true,
      },
      {
        binding: 'tagNm',
        header: String(t('ut.label.Tag 명', 'Tag 명')),
        width: '*',
        isReadOnly: true,
      },
      {
        binding: 'dataPgscNm',
        header: String(t('ut.label.프로세스', '프로세스')),
        width: 100,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'msrUnitCd',
        header: String(t('ut.label.단위', '단위')),
        width: 100,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'utmTpNm',
        header: String(t('ut.label.UT 항목', 'UT 항목')),
        width: 140,
        align: 'center',
        isReadOnly: !hasAuth,
        cssClass: 'WijmoSelect',
        dataMap: utmTmNmCodeMap,
        cellTemplate: (ctx) => {
          return `<span>${utmTmNmCodeMap.getDisplayValue(ctx.text)}</span>`;
        },
      },
      {
        binding: 'stndEqpId',
        header: String(t('ut.label.표준설비코드', '표준설비코드')),
        width: 150,
        isReadOnly: false,
        align: 'center',
        cssClass: 'WijmoFind',
        isRequired: false,
        cellTemplate: (params) => `
            <span>${params.value || ''}</span> 
            <Button id="stndEqpId" />} 
        `,
      },
      {
        binding: 'maxNvl',
        header: String(t('ut.label.Peak값', 'Peak값')),
        width: 120,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'aplUserNm',
        header: String(t('ut.label.지정자', '지정자')),
        width: 120,
        isReadOnly: true,
        align: 'center',
        // cssClass: 'WijmoFind',
        cellTemplate: (params) => btnSelectAplUser({ ...params }),
      },
      {
        binding: 'aplUserId',
        isReadOnly: true,
        visible: false,
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일자', '수정일자')),
        width: 120,
        align: 'center',
        isReadOnly: true,
      },
    ];
  }, [rowData]);

  const onInitialized = (grid) => {
    new Selector(grid);
    setFlexRef(grid);
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    grid.hostElement.addEventListener('click', (e) => {
      const len = grid.rows.length;
      if (len == 0) return;
      const ht = grid.hitTest(e);
      grid.startEditing(true);

      if (ht.row < 0 || ht.col < 0) return;
      if (ht.panel.cellType != 1) return; // 선택된 영역이 셀이 아니면 나가리.

      const item = grid.rows[ht.row].dataItem; // 선택한 row의 정보
      setFlexItem(item);

      if (e.target instanceof HTMLButtonElement) {
        const id = e.target.id;
        if ('stndEqpId' === id) {
          setStandardEquipmentCondition({
            singleSelect: true,
            tagId: item.tagId,
            stndEqpId: item.stndEqpId,
          });
          setStandardEquipmentModalOpen(true);
        }
        if ('btnContPop' === id) {
          setUserIds(item.aplUserId);
          setOpenUserReadModal(true);
        }
      }
    });
    grid?.hostElement?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const currentRowIndex = grid.selection.row;
        const currentColIndex = grid.selection.col;
        const currentColumn = grid.columns[currentColIndex];
        if (currentColumn?.binding === 'stndEqpId') {
          setStandardEquipmentModalOpen(true);
          e.cancel = true;
          console.log('grid.activeEditor', grid.activeEditor);
          const cellValue = grid.activeEditor
            ? grid.activeEditor.value
            : grid.getCellData(currentRowIndex, currentColIndex, false);
          console.log('cellValue', cellValue);
          setStandardEquipmentCondition({
            ...standardEquipmentCondition,
            stndEqpId: cellValue,
          });
        }
      }
    });
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;

      if (item.crudKey !== CrudCode.CREATE) {
        if (['prdnProcCd', 'eqclId', 'dtalProcCd', 'eqpMainId', 'eqpMchId'].includes(binding)) {
          cell.ariaReadOnly = true;
          toggleClass(cell, 'WijmoSelect', !isReadOnly);
          if (isReadOnly && cell.firstElementChild instanceof HTMLButtonElement) {
            cell.firstChild.remove();
          }
        }
      }
    }
  });

  const handleOnChange = (name, value) => {
    setSearchParam({ ...searchParam, [name]: value });
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      btnSearch();
    }
  };

  const btnExcelExport = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = 'Ut Matrix EUM Tag ID';
    book.saveAsync(getExcelFileName(t('ut.label.EUM Tag ID', 'EUM Tag ID')));
  };

  const btnSearch = () => {
    getUtMatrixEumTagIdList(searchParam).then((result: UtMatrixEumTagId[]) => {
      if (result !== null) {
        setRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const btnReload = () => {
    setSearchParam(
      (prev) =>
        Object.keys(prev).reduce(
          (acc, cur) => Object.assign(acc, { [cur]: initSearchParam[cur] || '' }),
          {}
        ) as SearchParam
    );
  };

  const btnSelectAplUser = (params: any) => {
    // 담당자 변경 가능 - 신규, 재작성, 재작성(버전업)
    // 담당자 변경 불가 - 그 외
    // if (isOpenMode === 'READ' || isOpenMode === 'HOLD') {
    return `<span>${params.value || ''}</span><button id="btnContPop" />`;
    // } else {
    //   return `<span>${params.value || ''}</span><button id="btnElmEmpPopup" />`;
    // }
  };

  const btnSave = () => {
    const saveRowData = rowData.filter(
      (element) => element.crudKey == CrudCode.CREATE || element.crudKey == CrudCode.UPDATE
    );

    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: () => {
        const check = new Map();
        saveRowData.filter((item) => {
          const key = `${item.msrUnitCd}_${item.utmTpNm}_${item.stndEqpId}`;
          if (!check.has(key)) {
            check.set(key, true);
            return true;
          }
          check.set(key, false);
          return false;
        });

        let result = true;
        check.forEach((value, key) => {
          console.log('value', value);
          if (!value) {
            openMessageBar({
              type: 'error',
              content:
                t(
                  'com.label.동일한 단위-항목-표준설비코드가 존재합니다.',
                  '동일한 단위-항목-표준설비코드가 존재합니다.'
                ) +
                ' ' +
                key,
            });
            result = false;
            return result;
          }
        });

        if (!result) {
          return false;
        }
        checkDuplicationEumTagMapping(saveRowData).then((result) => {
          if (result) {
            openMessageBar({
              type: 'error',
              content:
                t(
                  'com.label.동일한 단위-항목-표준설비코드가 존재합니다.',
                  '동일한 단위-항목-표준설비코드가 존재합니다.'
                ) +
                ' ' +
                result,
            });
            return false;
          } else {
            saveUtMatrixEumTagList(saveRowData).then((result) => {
              if (!result) {
                openMessageBar({
                  type: 'error',
                  content: t(
                    'com.label.저장 중 오류가 발생했습니다.',
                    '저장 중 오류가 발생했습니다.'
                  ),
                });
                return false;
              } else {
                openMessageBar({
                  type: 'confirm',
                  content: t('com.label.저장되었습니다.', '저장되었습니다.'),
                });
                btnSearch();
              }
            });
          }
        });
      },
    });
  };

  return (
    <ContainerLayout>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('ut.label.법인', '법인'))}</label>
                <MultiComboBox
                  id="copCd"
                  options={copCdCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.copCds || []}
                  onChange={(value) => {
                    handleOnChange('copCd', value.join());
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.Tag ID', 'Tag ID')}</label>
                <CustomInputWithSearch
                  name="tagId"
                  value={searchParam.tagId || ''}
                  defaultValue={searchParam.tagId || ''}
                  placeholder={String(
                    t('com.label.Tag ID를 입력해 주세요.', 'Tag ID를 입력해 주세요.')
                  )}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={onKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.Tag 명', 'Tag 명')}</label>
                <CustomInputWithSearch
                  name="tagNm"
                  value={searchParam?.tagNm || ''}
                  defaultValue={searchParam?.tagNm || ''}
                  placeholder={String(
                    t('com.label.Tag 명을 입력해 주세요.', 'Tag 명을 입력해 주세요.')
                  )}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={onKeyDown}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows>
              <SearchCols>
                <label>{String(t('ut.label.UT 항목', 'UT 항목'))}</label>
                <MultiComboBox
                  id="utmTpNm"
                  options={utmTpNmCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.utmTpNms || []}
                  onChange={(value) => {
                    handleOnChange('utmTpNm', value.join());
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.단위', '단위')}</label>
                <CustomInputWithSearch
                  name="msrUnitCd"
                  value={searchParam?.msrUnitCd || ''}
                  defaultValue={searchParam?.msrUnitCd || ''}
                  placeholder={String(
                    t('com.label.단위를 입력해 주세요.', '단위를 입력해 주세요.')
                  )}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={onKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('ut.label.미지정', '미지정')}</label>
                <div style={{ display: 'inline-flex', paddingTop: '7px' }}>
                  <Checkbox
                    id="libYn"
                    name="libYn"
                    css={st.checkbox}
                    className="custom"
                    checked={searchParam?.stndEqpIdYn === 'Y'}
                    onClick={(e) => {
                      setSearchParam({
                        ...searchParam,
                        stndEqpIdYn: searchParam?.stndEqpIdYn === 'Y' ? 'N' : 'Y',
                      });
                    }}
                    disableRipple
                  />
                </div>
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <Button
              css={IconButton.button}
              className="reload"
              onClick={btnReload}
              disableRipple
            ></Button>
            <Button css={IconButton.button} className="find" onClick={btnSearch} disableRipple>
              {t('com.button.조회', '조회')}
            </Button>
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ut.label.EUM Tag ID List', 'EUM Tag ID List')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <ComboBox
            options={utmTpNmCode}
            placeholder={String(t('ut.label.UT 항목', 'UT 항목'))}
            onChange={(value) => {
              setUtmTpNmValue(value);
            }}
            style={{ width: '80' }}
          />
          <Button
            css={IconButton.button}
            className="setting"
            onClick={onBtUtmTpNmAll}
            disableRipple
          >
            {t('com.button.일괄적용', '일괄적용')}
          </Button>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={btnSearch} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        initialized={onInitialized}
        height={575}
        itemFormatter={onItemFormatter}
        isSelector={true}
        allowPinning={false}
        selectionMode={SelectionMode.Row}
        onChangeCheckedItem={(items) => setCheckedItems(items)}
        keyActionEnter={KeyAction.None}
      />
      <GlobalBtnGroup>
        {hasAuth && (
          <Button css={IconButton.button} className="save" onClick={btnSave}>
            {t('com.button.저장', '저장')}
          </Button>
        )}
      </GlobalBtnGroup>
      {isStandardEquipmentModalOpen && (
        <StandardEquipmentPopup
          open={isStandardEquipmentModalOpen}
          close={() => {
            setStandardEquipmentModalOpen(false);
          }}
          singleSelect={standardEquipmentCondition?.singleSelect}
          condition={{
            // prdnProcCd: '',
            // eqclId: '',
            tagId: standardEquipmentCondition?.tagId || '',
            stndEqpId: standardEquipmentCondition?.stndEqpId || '',
          }}
          selectedIds={standardEquipmentCondition?.selectedIds}
          onCallback={(result) => {
            if (_.isEmpty(result.stndEqpId)) {
              openMessageBar({
                type: 'error',
                content: t(
                  'ut.label.조회 후 표준설비코드 버튼을 눌러주세요.',
                  '조회 후 표준설비코드 버튼을 눌러주세요.'
                ),
              });
              return;
            }
            // 그리드 행 내 단일 선택
            flexItem['stndEqpId'] = result?.stndEqpId;
            flexItem['crudKey'] = CrudCode.UPDATE;
            gridRef.current.refresh();
          }}
        />
      )}
      {isOpenUserReadModal && (
        <GatingContListPopUp
          open={isOpenUserReadModal}
          close={() => setOpenUserReadModal(false)}
          title={t('com.label.지정자', '지정자')}
          initParam={userIds}
        />
      )}
    </ContainerLayout>
  );
};

export default UtMatrixEumTagIdPage;
